import FirebaseService from "services/FirebaseService"
import { AUTH_TOKEN, USER_TYPE, REFRESH_TOKEN } from "constants/AuthConstant"
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"

import AuthService from "../../services/AuthService"

const allowedPlanTypes = new Set(["monthly", "annual", "tester"])
const SUBSCRIPTION_NOTICE_KEY = "subscriptionRequestNotice"

export const DEFAULT_POST_LOGIN_REDIRECT = "/app/dashboards/default"

export const resolveRedirectTarget = (target) => {
  if (typeof target !== "string" || target.length === 0) {
    return null
  }

  const trimmed = target.trim()
  if (!trimmed.startsWith("/")) {
    return null
  }

  if (trimmed.startsWith("/auth/")) {
    return null
  }

  return trimmed
}

export const sanitizeRedirectTarget = (target) =>
  resolveRedirectTarget(target) ?? DEFAULT_POST_LOGIN_REDIRECT

const buildSubscriptionPayload = (planId, planType) => {
  if (!planId) {
    return null
  }

  const numericPlanId = Number(planId)
  if (!Number.isFinite(numericPlanId) || numericPlanId <= 0) {
    return null
  }

  if (typeof planType !== "string") {
    return { planId: numericPlanId }
  }

  const normalizedPlanType = planType.trim().toLowerCase()
  if (!allowedPlanTypes.has(normalizedPlanType)) {
    return { planId: numericPlanId }
  }

  return {
    planId: numericPlanId,
    planType: normalizedPlanType,
  }
}

export const initialState = {
  loading: false,
  message: "",
  redirect: "",
  showMessage: false,
  token: localStorage.getItem(AUTH_TOKEN) || null,
}

export const signIn = createAsyncThunk(
  "user/signIn",
  async (data, { rejectWithValue }) => {
    const { email, password, isDoctor, planId, planType } = data
    try {
      // Call the AuthService.login with the correct endpoint based on isDoctor
      const response = await AuthService.login({ email, password, isDoctor })
      if (response) {
        const token = response.token
        localStorage.setItem(AUTH_TOKEN, token)
        localStorage.setItem(REFRESH_TOKEN, response.refresh_token)
        localStorage.setItem(USER_TYPE, isDoctor ? "doctor" : "user")

        if (isDoctor) {
          const subscriptionPayload = buildSubscriptionPayload(planId, planType)
          if (subscriptionPayload) {
            try {
              await AuthService.requestSubscription(subscriptionPayload)
            } catch (subscriptionError) {
              console.error("Subscription request failed", subscriptionError)
            } finally {
              localStorage.setItem(
                SUBSCRIPTION_NOTICE_KEY,
                JSON.stringify({
                  status: "pending",
                  timestamp: Date.now(),
                })
              )
            }
          }
        }
        return token
      } else {
        return rejectWithValue(response.message?.replace("Firebase: ", ""))
      }
    } catch (err) {
      return rejectWithValue(err.message || "Error")
    }
  }
)

export const signUp = createAsyncThunk(
  "user/signUp",
  async (data, { rejectWithValue }) => {
    const { confirm, confirmPassword, planId, planType, queryString, ...rest } =
      data

    if ("redirect" in rest) {
      delete rest.redirect
    }

    const payload = {
      ...rest,
      confirmPassword: confirmPassword ?? confirm,
    }

    if (!payload.confirmPassword) {
      delete payload.confirmPassword
    }

    try {
      const response = await AuthService.register(payload)

      const token = response?.token
      const refreshToken = response?.refresh_token

      if (token) {
        localStorage.setItem(AUTH_TOKEN, token)
      }
      if (refreshToken) {
        localStorage.setItem(REFRESH_TOKEN, refreshToken)
      }
      if (token || refreshToken) {
        localStorage.setItem(USER_TYPE, "doctor")
      }

      const subscriptionPayload = buildSubscriptionPayload(planId, planType)
      if (subscriptionPayload) {
        try {
          await AuthService.requestSubscription(subscriptionPayload)
          localStorage.setItem(
            SUBSCRIPTION_NOTICE_KEY,
            JSON.stringify({
              status: "pending",
              timestamp: Date.now(),
            })
          )
        } catch (subscriptionError) {
          console.error("Subscription request failed", subscriptionError)
          localStorage.setItem(
            SUBSCRIPTION_NOTICE_KEY,
            JSON.stringify({
              status: "pending",
              timestamp: Date.now(),
            })
          )
        }
      }

      localStorage.removeItem(AUTH_TOKEN)
      localStorage.removeItem(REFRESH_TOKEN)
      localStorage.removeItem(USER_TYPE)

      const querySegment = (() => {
        if (typeof queryString !== "string" || queryString.length === 0) {
          return ""
        }

        const rawQuery = queryString.startsWith("?")
          ? queryString.slice(1)
          : queryString

        if (!rawQuery) {
          return ""
        }

        const params = new URLSearchParams(rawQuery)
        params.delete("planId")
        params.delete("planType")

        const serialized = params.toString()
        return serialized ? `?${serialized}` : ""
      })()

      return { redirect: `/auth/login${querySegment}` }
    } catch (err) {
      return rejectWithValue(err.message || "Error")
    }
  }
)

export const signOut = createAsyncThunk("auth/signOut", async () => {
  const response = await FirebaseService.signOutRequest()
  localStorage.removeItem(AUTH_TOKEN)
  return response.data
})

export const authSlice = createSlice({
  extraReducers: (builder) => {
    builder
      .addCase(signIn.pending, (state) => {
        state.loading = true
      })
      .addCase(signIn.fulfilled, (state, action) => {
        state.loading = false
        state.redirect = sanitizeRedirectTarget(action.meta?.arg?.redirect)
        state.token = action.payload
      })
      .addCase(signIn.rejected, (state, action) => {
        state.message = action.payload
        state.showMessage = true
        state.loading = false
      })
      .addCase(signOut.fulfilled, (state) => {
        state.loading = false
        state.token = null
        state.redirect = "/"
      })
      .addCase(signOut.rejected, (state) => {
        state.loading = false
        state.token = null
        state.redirect = "/"
      })
      .addCase(signUp.pending, (state) => {
        state.loading = true
        state.redirect = ""
      })
      .addCase(signUp.fulfilled, (state, action) => {
        state.loading = false
        state.redirect = action.payload?.redirect || "/auth/login"
        state.token = null
      })
      .addCase(signUp.rejected, (state, action) => {
        state.message = action.payload
        state.showMessage = true
        state.loading = false
        state.redirect = ""
      })
  },
  initialState,
  name: "auth",
  reducers: {
    authenticated: (state, action) => {
      state.loading = false
      state.redirect = "/"
      state.token = action.payload
    },
    hideAuthMessage: (state) => {
      state.message = ""
      state.showMessage = false
    },
    clearRedirect: (state) => {
      state.redirect = ""
    },
    showAuthMessage: (state, action) => {
      state.message = action.payload
      state.showMessage = true
      state.loading = false
    },
    showLoading: (state) => {
      state.loading = true
    },
    signInSuccess: (state, action) => {
      state.loading = false
      state.token = action.payload
    },
    signOutSuccess: (state) => {
      state.loading = false
      state.token = null
      state.redirect = "/"
    },
    setToken: (state, action) => {
      state.token = action.payload
    },
  },
})

export const {
  authenticated,
  clearRedirect,
  hideAuthMessage,
  showAuthMessage,
  showLoading,
  signInSuccess,
  signOutSuccess,
  setToken,
} = authSlice.actions

export const SUBSCRIPTION_NOTICE_STORAGE_KEY = SUBSCRIPTION_NOTICE_KEY

export default authSlice.reducer
